import { ArtifactFocus, recommendedFocusWidth, type ArtifactFocusItem } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

/**
 * Verification harness (added by the screenshot-verify pass for #126 / PR #166).
 * The drill-in detail view for one Brain artifact — the expanded counterpart to
 * ArtifactTile. A 2-column bento: dark hero stage (per-type identity color +
 * close) / preview / details / sources / type-aware actions.
 *
 * #126 sign-off gate (verify in a real browser, light + .dark):
 *  - experience kinds carry per-type identity color ONLY in the hero icon tile
 *    + label dot; everything else stays monochrome;
 *  - the primary CTA computes from status + liveUrl ("Open draft" while draft,
 *    "Open live" once published);
 *  - non-experience kinds fall back to the lightweight per-kind preview
 *    stand-in when no `preview` slot is supplied;
 *  - the hero aurora + transform-only entrance are reduced-motion safe.
 * Each block is sized to `recommendedFocusWidth(item)` (the grow-only hint the
 * host would feed AppShell's Brain region).
 */

// A stand-in for the AIStudio-v3 CenterStage the host would pass via `preview`.
function PreviewSlot({ note }: { note: string }) {
  return (
    <div className="flex h-full min-h-[140px] w-full flex-col items-center justify-center gap-1 rounded-[10px] border border-dashed border-border/70 bg-muted/40 p-4 text-center">
      <span className="text-sm font-medium text-foreground">Host preview slot</span>
      <span className="text-xs text-muted-foreground">{note}</span>
    </div>
  );
}

const PUBLISHED_QUEST: ArtifactFocusItem = {
  id: "q-1",
  title: "Cell Biology Mastery Quiz",
  label: "Quiz",
  subtitle: "Generated by AIKO",
  summary: "MCQ · 10 questions",
  kind: "experience",
  expType: "quest",
  grounded: true,
  groundLabel: "3 sources",
  facts: [
    ["Type", "Quiz · MCQ"],
    ["Questions", "10"],
    ["Difficulty", "Intermediate"],
    ["Updated", "2 hours ago"],
  ],
  sources: { label: "Grounded on 3 sources", chips: ["Lecture 4.pdf", "Chapter 7", "Lab notes"] },
  status: "published",
  liveUrl: "https://example.com/q-1",
  relation: "published",
};

const DRAFT_NOTE: ArtifactFocusItem = {
  id: "n-1",
  title: "Photosynthesis — study note",
  label: "Note",
  subtitle: "Generated by AIKO",
  summary: "Long-form · 6 sections",
  kind: "experience",
  expType: "note",
  grounded: true,
  groundLabel: "2 sources",
  facts: [
    ["Type", "Study note"],
    ["Sections", "6"],
    ["Status", "Draft"],
    ["Updated", "just now"],
  ],
  sources: { label: "Grounded on 2 sources", chips: ["Biology 101", "Khan transcript"] },
  status: "draft",
  liveUrl: null,
  relation: "created",
};

const UPLOADED_FILE: ArtifactFocusItem = {
  id: "f-1",
  title: "Q3 Market Research.pdf",
  label: "PDF",
  subtitle: "Uploaded · indexed",
  summary: "PDF · 24 pages",
  kind: "file",
  grounded: false,
  facts: [
    ["Type", "PDF document"],
    ["Pages", "24"],
    ["Size", "3.2 MB"],
    ["Indexed", "yes"],
  ],
  sources: { label: "Used in 2 experiences", chips: ["Market Quiz", "Trends Note"] },
  status: "ready",
  liveUrl: null,
  relation: "uploaded",
};

const noop = () => {};

export function ArtifactFocusDemo() {
  return (
    <Section
      id="artifact-focus"
      title="ArtifactFocus"
      description="The drill-in detail view for one Brain artifact (#126) — the expanded counterpart to ArtifactTile. A 2-column bento (hero stage / preview / details / sources / type-aware actions) over one mapped ArtifactFocusItem, emitting onAction / onClose. Per-type color is identity-only (hero icon tile + label dot). The primary CTA flips from 'Open draft' to 'Open live' in place once a draft publishes. The real experience body arrives via the `preview` slot; non-experience kinds get a lightweight per-kind stand-in. Token-driven, reduced-motion safe."
    >
      <VariantBlock
        label="Experience · published quest (CTA = Open live, with host preview slot)"
        caption={`Per-type identity color (quest → violet) in the hero tile + dot. Width ${recommendedFocusWidth(PUBLISHED_QUEST)}px.`}
      >
        <div style={{ width: recommendedFocusWidth(PUBLISHED_QUEST) }}>
          <ArtifactFocus
            item={PUBLISHED_QUEST}
            preview={<PreviewSlot note="The AIStudio-v3 CenterStage renders here in the app." />}
            onAction={noop}
            onClose={noop}
          />
        </div>
      </VariantBlock>

      <VariantBlock
        label="Experience · draft note (CTA = Open draft)"
        caption={`Draft status + liveUrl null drives the 'Open draft' CTA. Width ${recommendedFocusWidth(DRAFT_NOTE)}px.`}
      >
        <div style={{ width: recommendedFocusWidth(DRAFT_NOTE) }}>
          <ArtifactFocus
            item={DRAFT_NOTE}
            preview={<PreviewSlot note="Draft note body (host-rendered)." />}
            onAction={noop}
            onClose={noop}
          />
        </div>
      </VariantBlock>

      <VariantBlock
        label="Supporting kind · uploaded file (per-kind preview stand-in)"
        caption={`No preview slot → the lib's monochrome per-kind stand-in. Width ${recommendedFocusWidth(UPLOADED_FILE)}px.`}
      >
        <div style={{ width: recommendedFocusWidth(UPLOADED_FILE) }}>
          <ArtifactFocus item={UPLOADED_FILE} onAction={noop} onClose={noop} />
        </div>
      </VariantBlock>

      <VariantBlock
        label="PQA#2615 preview layout · full-height header cover, right-side tags + studio hint, actions below the header, editable tags between preview and Details"
        caption="The owner's redesigned preview order: HEADER (full-height cover media panel + right-side taxonomy) → status + actions → preview → the host's editable-tags slot → Details/Sources. headerMedia would carry the host's live cover component (upload + AI-generate dropdown) in the app."
      >
        <div style={{ width: recommendedFocusWidth(PUBLISHED_QUEST) }}>
          <ArtifactFocus
            item={{
              ...PUBLISHED_QUEST,
              cover:
                "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 160 100'%3E%3Crect width='160' height='100' fill='%233b2f63'/%3E%3Ccircle cx='120' cy='30' r='34' fill='%23a855f7' opacity='0.55'/%3E%3Ccircle cx='40' cy='80' r='40' fill='%236384ff' opacity='0.5'/%3E%3C/svg%3E",
              tags: ["biology", "cells", "grade-9"],
            }}
            actionsPlacement="belowHero"
            headerExtra={
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-[3px] text-[10.5px] text-primary-foreground/85">
                in Bio Studio
              </span>
            }
            belowPreview={
              <div className="rounded-[13px] border border-dashed border-border/70 bg-muted/40 px-3.5 py-3 text-xs text-muted-foreground">
                Host editable-tags slot (TagInput lives here in the app)
              </div>
            }
            preview={<PreviewSlot note="Preview body — tags slot renders right below me." />}
            onAction={noop}
            onClose={noop}
          />
        </div>
      </VariantBlock>

      <VariantBlock
        label="headerMedia slot · host cover component with an OPEN generate dropdown (the overflow contract)"
        caption="The slot form of the media panel is deliberately NOT overflow-clipped: a host dropdown must ESCAPE the small panel (and the hero) to be usable, while the host content clips its OWN corners to the panel's 13px radius. This block pins both halves visually: the mock menu renders fully outside the panel + hero, and the mock cover's corners stay rounded. In the app (uni#581) this slot carries ExperienceCover layout='hero' with the live AI-generate style dropdown."
      >
        <div style={{ width: recommendedFocusWidth(PUBLISHED_QUEST) }}>
          <ArtifactFocus
            item={{ ...PUBLISHED_QUEST, tags: ["biology", "cells"] }}
            actionsPlacement="belowHero"
            headerMedia={
              <div className="relative flex h-full min-h-[104px] w-full flex-col justify-end">
                {/* Host content self-clips its corners (the slot box does not). */}
                <div className="absolute inset-0 overflow-hidden rounded-[13px] bg-[linear-gradient(135deg,#3b2f63,#1c2f5e)]" />
                <div className="relative z-[1] flex flex-wrap items-center gap-1.5 rounded-b-[13px] bg-[linear-gradient(to_top,rgba(10,37,64,0.82),transparent)] px-2 pb-2 pt-6">
                  <span className="inline-flex h-6 items-center gap-1 rounded-full border border-white/30 bg-white/10 px-2 text-[11px] font-medium text-white">
                    ✦ Generate ▾
                  </span>
                </div>
                {/* Mock OPEN dropdown: renders OUTSIDE the panel and the hero —
                    the exact escape the un-clipped slot/hero exist for. */}
                <div className="absolute left-1 top-[calc(100%+6px)] z-50 w-40 rounded-xl border border-border bg-popover p-1 text-left shadow-lg">
                  <div className="px-2 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                    Generate with style
                  </div>
                  {["Auto", "Cinematic", "Minimal", "Vibrant"].map((s, i) => (
                    <div
                      key={s}
                      className={`rounded-lg px-2 py-1.5 text-[12.5px] ${i === 0 ? "bg-muted text-foreground" : "text-muted-foreground"}`}
                    >
                      {s}
                    </div>
                  ))}
                </div>
              </div>
            }
            preview={
              <PreviewSlot note="The open menu above must overlay me, not clip at the hero edge." />
            }
            onAction={noop}
            onClose={noop}
          />
        </div>
      </VariantBlock>
    </Section>
  );
}
